
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Mail, Phone, Edit, Trash2, BarChart2, Activity } from "lucide-react";
import { useStudentDetails } from "@/hooks/useStudentDetails";
import { useStudentAttendanceStats } from "@/hooks/useStudentAttendanceStats";
import { useStudentActivitiesSchedule } from "@/hooks/useStudentActivitiesSchedule";

interface StudentCardProps {
  student: any;
  onEdit: (student: any) => void;
  onDelete: (student: any) => void;
  onViewHistory: (student: any) => void;
}

export const StudentCard = ({ student, onEdit, onDelete, onViewHistory }: StudentCardProps) => {
  // Fetch detailed information only when the card is rendered
  const { data: details } = useStudentDetails(student.id);
  const { data: stats } = useStudentAttendanceStats(student.id);
  
  // Get active activities for this student
  const activeActivities = details?.student_activities?.filter((sa: any) => sa.status === 'active') || [];
  const activityIds = activeActivities.map((sa: any) => sa.activity_id);
  
  // Fetch schedules for all activities in a single query
  const { data: schedulesByActivity } = useStudentActivitiesSchedule(student.id, activityIds);
  
  // Avatar: iniciales si no hay foto
  const getInitials = (name: string) => {
    if (!name) return "??";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  // Mapeo de días de la semana (0=Domingo, 1=Lunes, etc.)
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  return (
    <Card className="w-full max-w-md shadow-card hover:shadow-lg transition-shadow">
      <CardHeader className="flex flex-row items-center gap-4 pb-2">
        <Avatar className="w-16 h-16">
          <AvatarFallback className="text-lg font-bold bg-primary/10">
            {getInitials(student.name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <CardTitle className="text-lg font-bold line-clamp-1">{student.name}</CardTitle>
          <div className="text-muted-foreground text-sm">Clase: {student.class}</div>
          {student.status && (
            <Badge variant={student.status === 'active' ? 'default' : 'secondary'} className="mt-1">
              {student.status}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Información de contacto */}
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm">
            <Mail className="w-4 h-4 text-muted-foreground" />
            <span className="truncate">
              {student.email || <span className="text-muted-foreground">Sin email</span>}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Phone className="w-4 h-4 text-muted-foreground" />
            <span className="truncate">
              {student.phone || <span className="text-muted-foreground">Sin teléfono</span>}
            </span>
          </div>
        </div>

        {/* Actividades con horarios específicos */}
        <div className="space-y-2">
          <div className="text-sm font-medium">Actividades:</div>
          <div className="space-y-2">
            {activeActivities.length > 0 ? (
              activeActivities.map((sa: any) => {
                const activity = sa.activities;
                const schedule = schedulesByActivity?.[sa.activity_id] || [];
                
                return (
                  <div key={sa.activity_id} className="bg-muted/50 p-2 rounded-md">
                    <div className="flex items-center gap-1 mb-1">
                      <Activity className="w-3 h-3" />
                      <span className="text-sm font-medium">{activity?.name || 'Sin nombre'}</span>
                    </div>
                    
                    {/* Mostrar horarios específicos si existen */}
                    {schedule.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {schedule.map((day: number, dayIndex: number) => (
                          <Badge key={dayIndex} variant="outline" className="text-xs">
                            {dayNames[day]}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    {/* Si no tiene horarios específicos, mostrar horarios generales de la actividad */}
                    {schedule.length === 0 && activity?.days_of_week && (
                      <div className="flex flex-wrap gap-1">
                        {activity.days_of_week.map((day: number, dayIndex: number) => (
                          <Badge key={dayIndex} variant="secondary" className="text-xs">
                            {dayNames[day]}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    {/* Horario de la actividad */}
                    {(activity?.start_time && activity?.end_time) && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {activity.start_time} - {activity.end_time}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <span className="text-muted-foreground text-xs">Sin actividades</span>
            )}
          </div>
        </div>

        {/* Estadísticas de asistencia */}
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <div className="bg-green-50 dark:bg-green-900/20 p-2 rounded">
            <div className="font-bold text-green-700 dark:text-green-300">
              {stats?.present ?? 0}
            </div>
            <div className="text-green-600 dark:text-green-400">Presentes</div>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 p-2 rounded">
            <div className="font-bold text-red-700 dark:text-red-300">
              {stats?.absent ?? 0}
            </div>
            <div className="text-red-600 dark:text-red-400">Ausentes</div>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded">
            <div className="font-bold text-blue-700 dark:text-blue-300">
              {stats?.attendanceRate ?? '-'}
            </div>
            <div className="text-blue-600 dark:text-blue-400">% Asist.</div>
          </div>
        </div>

        {/* Notas adicionales */}
        {student.notes && (
          <div className="mt-2 p-2 bg-muted rounded text-xs text-muted-foreground">
            <strong>Notas:</strong> {student.notes}
          </div>
        )}

        {/* Botones de acción */}
        <div className="flex gap-2 mt-4 pt-2 border-t">
          <Button size="sm" variant="outline" onClick={() => onEdit(student)} className="flex-1">
            <Edit className="w-4 h-4 mr-1" />
            Editar
          </Button>
          <Button size="sm" variant="destructive" onClick={() => onDelete(student)}>
            <Trash2 className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="secondary" onClick={() => onViewHistory(student)}>
            <BarChart2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
